import { useEffect, useState } from "react";
import {
  HOLDER_COLOR_PALETTE,
  PLAYER_COLOR_PALETTE,
  isHolderColor,
} from "@solblox/shared";

import { DraggableOverlay } from "../util/DraggableOverlay";
import { useIsTouch } from "../util/useIsTouch";
import { useStore } from "../store";
import { sendColorSet } from "../net/colyseus";
import { resolveSkinColor } from "./skinColor";

/**
 * Pointer-lock blocks all DOM interaction — cursor is hidden and trapped in
 * the canvas, so the user literally cannot move the mouse onto a HUD button.
 * Every SkinPicker click handler exits lock first so the popover and swatches
 * are reachable as soon as the user moves toward the picker button (the same
 * click that opens the popover also frees the cursor).
 */
function releasePointerLock(): void {
  if (typeof document === "undefined") return;
  if (document.pointerLockElement) document.exitPointerLock?.();
}

export function SkinPicker(): JSX.Element | null {
  const isTouch = useIsTouch();
  const skinColorHex = useStore((s) => s.skinColorHex);
  const localPlayer = useStore((s) => s.localPlayer);
  const isSpectator = localPlayer?.isSpectator === true;
  const isHolder = localPlayer?.isHolder === true;
  const [open, setOpen] = useState(false);

  // Holder-exclusive colors are rejected by the server at join time (holder
  // status isn't verified yet). Once verified, re-apply a persisted holder pick
  // so the special finish survives a reload.
  useEffect(() => {
    if (isHolder && skinColorHex && isHolderColor(skinColorHex)) {
      sendColorSet(skinColorHex);
    }
  }, [isHolder, skinColorHex]);

  if (!localPlayer || isSpectator) return null;

  const identity = localPlayer.pubkey || localPlayer.id || "";
  const current = resolveSkinColor(skinColorHex ?? localPlayer.colorHex ?? "", identity);
  const currentIsHolder = isHolderColor(current);

  const onToggle = (): void => {
    releasePointerLock();
    setOpen((o) => !o);
  };

  const onPick = (hex: string): void => {
    useStore.getState().setSkinColorHex(hex);
    sendColorSet(hex);
    setOpen(false);
  };

  return (
    <DraggableOverlay
      id="skin-picker"
      defaultAnchor={{ right: 12, top: 130, safeAreaTop: isTouch }}
      enabled={isTouch}
      // Stacked above MicHud (z=20) so an opened mic-settings popover can't
      // occlude the swatch grid — both anchor to the right column.
      zIndex={25}
      nonDragSelector="button"
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          alignItems: "flex-end",
          userSelect: "none",
          pointerEvents: "auto",
        }}
      >
        <button
          onClick={onToggle}
          title="Change avatar color"
          aria-label="Change avatar color"
          style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            border: "2px solid #2a2a36",
            background: current,
            cursor: "pointer",
            boxShadow: currentIsHolder
              ? `0 0 10px ${current}, 0 1px 4px rgba(0,0,0,0.5)`
              : "0 1px 4px rgba(0,0,0,0.5)",
            boxSizing: "border-box",
            padding: 0,
          }}
        />
        {open && (
          <div
            style={{
              padding: 10,
              background: "rgba(15,16,24,0.94)",
              border: "1px solid #2a2a36",
              borderRadius: 8,
              display: "flex",
              flexDirection: "column",
              gap: 8,
              width: 168,
            }}
          >
            <div style={labelStyle}>COLORS</div>
            <div style={gridStyle}>
              {PLAYER_COLOR_PALETTE.map((hex) => (
                <Swatch
                  key={hex}
                  hex={hex}
                  selected={hex === current}
                  onPick={() => onPick(hex)}
                />
              ))}
            </div>

            <div style={{ ...labelStyle, color: "#f4d36a" }}>✦ HOLDER COLORS</div>
            <div style={gridStyle}>
              {HOLDER_COLOR_PALETTE.map((hex) => (
                <Swatch
                  key={hex}
                  hex={hex}
                  selected={hex === current}
                  finish
                  locked={!isHolder}
                  onPick={() => isHolder && onPick(hex)}
                />
              ))}
            </div>
            {!isHolder && (
              <div style={{ fontSize: 10.5, color: "#9aa0b4", lineHeight: 1.4 }}>
                Hold the token to unlock the shiny finishes.
              </div>
            )}
          </div>
        )}
      </div>
    </DraggableOverlay>
  );
}

function Swatch({
  hex,
  selected,
  onPick,
  finish = false,
  locked = false,
}: {
  hex: string;
  selected: boolean;
  onPick: () => void;
  finish?: boolean;
  locked?: boolean;
}): JSX.Element {
  return (
    <button
      onClick={onPick}
      title={locked ? "Holders only" : hex}
      aria-label={`Pick ${hex}`}
      disabled={locked}
      style={{
        position: "relative",
        width: 28,
        height: 28,
        borderRadius: 14,
        border: selected ? "2px solid #ffd180" : "2px solid #3a3a48",
        background: hex,
        cursor: locked ? "not-allowed" : "pointer",
        padding: 0,
        boxSizing: "border-box",
        opacity: locked ? 0.38 : 1,
        // Metallic-ish sheen + glow preview for holder finishes.
        boxShadow: finish && !locked ? `0 0 8px ${hex}` : "none",
        backgroundImage: finish
          ? "linear-gradient(135deg, rgba(255,255,255,0.55), rgba(255,255,255,0) 45%)"
          : undefined,
      }}
    >
      {locked && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
          }}
        >
          🔒
        </span>
      )}
    </button>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.6,
  color: "#9aa0b4",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 28px)",
  gap: 8,
};
